// src/config/twynpopDictionary.js

// -----------------------------------------------------------------------------
// Human-readable labels for DEMOGRAPHIC filters (multi-selects)
// -----------------------------------------------------------------------------
export const demographicVarLabels = {
  ageband: "Age",
  Sex: "Sex",
  Race: "Race",
  Eth: "Ethnicity",
  incband: "Household Income",
  npband: "Household Size",
  tenband: "Housing Tenure",
  bldband: "Building Type",
  yrbltband: "Construction Age",
  hflband: "Heating Fuel",
};

// -----------------------------------------------------------------------------
// Human-readable labels for TARGET PACKAGES
// -----------------------------------------------------------------------------
export const targetPackageLabels = {
  Insurance: "Insurance",
  "Family & Life": "Family & Life",
  Support: "Support",
  "Work & Transit": "Work & Transit",
  Housing: "Housing",
};

// -----------------------------------------------------------------------------
// Human-readable labels for TARGET / CONTEXT variables
// -----------------------------------------------------------------------------
export const targetVarLabels = {
  // Access & basic services
  internet_access: "Internet",
  smartphone_access: "Smartphone",
  laptop_desktop: "Laptop or Desktop",
  moved_in: "Length of Residence",
  n_stories: "Number of Stories",
  snap_yearly: "SNAP Recipient",
  meals_in_rent: "Meals Included in Rent",
  refrigerator: "Refrigerator",
  sink_faucet: "Sink with Faucet",
  stove_range: "Stove or Range",
  complete_kitchen: "Complete Kitchen",

  // Vehicles / household composition
  vehicles_number: "Number of Vehicles",
  vehicles_available: "Vehicles Available",

  marital_status: "Marital Status",
  couple_type: "Couple Type",
  family_type: "Family Type",
  family_children: "Children Present",
  household_language: "Household Language",
  limited_english: "Limited English Proficiency",
  multigenerational_home: "Multigenerational Household",

  // Citizenship
  citizenship: "Citizenship Status",

  // Education & work
  employment_status: "Employment Status",
  industry_code: "Industry Code",
  worker_class: "Worker Class",
  school_status: "Student Status",
  educational_attainment: "Educational Attainment",

  // Commute
  commute_mode: "Commute Mode",
  commute_range: "Commute Time",

  // Disability
  disability_status: "Disability Status",

  // Health insurance
  hins_status: "Health Insurance Status",

  // Other insurance
  fire_hazard_flood_insurance: "Fire / Hazard / Flood Insurance",

  // Income supports
  public_assistance_income: "Income Assistance",
  
  // Energy Assistance
  energy_assist: "Energy Assistance",

  // Insulation Quality
  insulated: "Insulation Quality",

  // Water heaters
  water_heater: "Water Heater",

  // HVAC
  main_ac_equp: "Cooling",
  heating: "Heating",
  heat_pumps: "Heat Pump",

  // Roof windows
  attic: "Attic",
  windows: "Windows",
  high_ceil: "High Ceiling",
  roof_mat: "Roof Material",

  // ---------------------------------------------------------------------------
  // NEW (Geo Overlays) — matches backend key: "hardware_stores"
  // Only appears when backend reports available.hardware_stores === true,
  // and only for Kidde/Carrier.
  // ---------------------------------------------------------------------------
  hardware_stores: "Hardware Stores",
  food_stores: "Grocery Stores",
};

// -----------------------------------------------------------------------------
// Child variable groups used for UI logic
// -----------------------------------------------------------------------------

// Disability sub-dimensions (only relevant when disability_status = "with_disability")
export const disabilityChildVars = {
  hearing_diff: "Hearing Difficulty",
  vision_diff: "Vision Difficulty",
  cognitive_diff: "Cognitive Difficulty",
  ambulatory_diff: "Ambulatory Difficulty",
  self_care_diff: "Self Care Difficulty",
  indep_living_diff: "Independent Living Difficulty",
};

// High-level insurance “class” variables (used when hins_status = "yes")
export const hinsClassVars = {
  privcov: "Private Coverage",
  pubcov: "Public Coverage",
};

// Specific insurance source variables (children of hins_status = "yes")
export const hinsSourceVars = {
  hins_emp_union: "Employer / Union",
  hins_direct: "Direct Purchase",
  hins_medicare: "Medicare",
  hins_medicaid: "Medicaid",
  hins_tricare: "TRICARE / Military",
  hins_va: "VA Health Care",
  hins_ihs: "Indian Health Service",
};

// Water heater attributes (children of water_heater pseudo-parent)
export const waterHeaterVars = {
  main_water_heat_loc: "Water Heater Location",
  main_water_heat_size: "Water Heater Size",
  main_water_heat_blanket: "Water Heater Insulation",
  main_water_heat_age: "Water Heater Age",
  main_water_heat_fuel: "Water Heater Fuel Type",
};

// Heating attributes (children of heating pseudo-parent) 
export const heatingVars = {
  main_space_heat_equip: "Primary Heating Equipment",
  main_space_heat_equip_age: "Heating Equipment Age",
};

// Heat Pump attributes (children of heat_pumps pseudo-parent) 
export const heatPumpVars = {
  hp_status: "Heat Pump Eligibility",
  n_ductless_heatpumps: "Number of Ductless Units",
};

// Windows attributes — children of windows pseudo-parent
export const windowVars = {
  orig_win: "Original Windows",
  type_glass: "Window Glass Type",
};

// -----------------------------------------------------------------------------
// Level labels for each coded variable
// Map: variableName -> { codedValue: "Human label", ... }
// -----------------------------------------------------------------------------
export const levelsMap = {
  // ------------------------------
  // Synthetic population bands
  // ------------------------------
  ageband: {
    u5: "<5 years",
    a5_17: "5–17 years",
    a18_24: "18–24 years",
    a25_44: "25–44 years",
    a45_54: "45–54 years",
    a55_64: "55–64 years",
    a65_74: "65–74 years",
    a75plus: "75+ years",
  },

  Sex: {
    female: "Female",
    male: "Male",
  },

  Race: {
    white: "White",
    black: "Black",
    asian: "Asian",
    native: "Native American / Alaska Native",
    other: "Other Race",
  },

  Eth: {
    hispanic: "Hispanic / Latino",
    not_hispanic: "Not Hispanic / Latino",
  },

  incband: {
    iu10000: "<$10,000",
    i10000_14999: "$10,000–$14,999",
    i15000_24999: "$15,000–$24,999",
    i25000_34999: "$25,000–$34,999",
    i35000_49999: "$35,000–$49,999",
    i50000_74999: "$50,000–$74,999",
    i75000_99999: "$75,000–$99,999",
    i100000_149999: "$100,000–$149,999",
    i150000_199999: "$150,000–$199,999",
    i200000plus: "$200,000+",
  },

  npband: {
    np_1: "1 person",
    np_2: "2 people",
    np_3: "3 people",
    np_4plus: "4+ people",
  },

  tenband: {
    rent: "Renter-Occupied",
    own: "Owner-Occupied",
  },

  bldband: {
    btsfh_detached: "Single-Family Detached",
    btsfh_attached: "Single-Family Attached",
    bt2_4_apt: "2–4 Unit Apt/Condo",
    bt5_19_apt: "5–19 Unit Apt/Condo",
    bt20plus_apt: "20+ Unit Apt/Condo",
    btmh: "Mobile Home",
  },

  yrbltband: {
    bau1950: "<1950",
    ba1950_1979: "1950–1979",
    ba1980_1999: "1980–1999",
    ba2000plus: "2000+",
  },

  hflband: {
    natural_gas: "Natural Gas",
    electric_renewables: "Electric / Renewables",
    no_fuel: "No Fuel",
    oil_coal: "Oil / Coal",
    biomass: "Biomass",
    other_fuel: "Other Fuel",
  },

  // ------------------------------
  // Access & basic services
  // ------------------------------
  internet_access: { yes: "Yes", no: "No" },
  smartphone_access: { yes: "Yes", no: "No" },
  laptop_desktop: { yes: "Yes", no: "No" },
  snap_yearly: { yes: "Yes", no: "No" },
  meals_in_rent: { yes: "Yes", no: "No" },
  complete_kitchen: { yes: "Yes", no: "No" },
  stove_range: { yes: "Yes", no: "No" },
  sink_faucet: { yes: "Yes", no: "No" },
  refrigerator: { yes: "Yes", no: "No" },

  moved_in: {
    "0-12_months": "0–12 months",
    "13-23_months": "13–23 months",
    "2-4_years": "2–4 years",
    "5-9_years": "5–9 years",
    "10-19_years": "10–19 years",
    "20-29_years": "20–29 years",
    "30+_years": "30+ years",
  },

  n_stories: {
    "1": "1 story",
    "2": "2 stories",
    "3": "3 stories",
    split: "Split level",
    other: "Other",
  },

  // ------------------------------
  // Vehicles / household
  // ------------------------------
  vehicles_number: {
    "0": "0 vehicles",
    "1": "1 vehicle",
    "2": "2 vehicles",
    "3": "3 vehicles",
    "4": "4 vehicles",
    "5": "5 vehicles",
    "6+": "6+ vehicles",
  },

  vehicles_available: { yes: "Yes", no: "No" },

  // ------------------------------
  // Family / household structure
  // ------------------------------
  marital_status: {
    married: "Married",
    single: "Not Married",
  },

  couple_type: {
    opposite_sex_married: "Opposite-Sex Married Couple",
    same_sex_married: "Same-Sex Married Couple",
    opposite_sex_partners: "Opposite-Sex Unmarried Partners",
    same_sex_partners: "Same-Sex Unmarried Partners",
  },

  family_type: {
    married_couple: "Married-Couple Family",
    other_family: "Other Family",
    roommates: "Non-Family",
    live_alone: "Lives Alone",
  },

  family_children: {
    no_children: "No Children",
    "children_0-4_years": "Children 0–4 years",
    "children_5-17_years": "Children 5–17 years",
    "children_0-17_years": "Children 0–17 years",
  },

  household_language: {
    english: "English",
    spanish: "Spanish",
    other_indo_european: "Other Indo-European",
    asian_pacific_islander: "Asian / Pacific Islander",
    other: "Other Language",
  },

  limited_english: {
    english_proficient: "English Proficient",
    english_limited: "Limited English Proficiency",
  },

  multigenerational_home: {
    yes: "Multigenerational Household",
    no: "Not Multigenerational",
  },

  // ------------------------------
  // Citizenship
  // ------------------------------
  citizenship: {
    born_US: "Born in the U.S.",
    born_US_territory: "Born in U.S. Territory",
    born_abroad_US_parents: "Born Abroad to U.S. Parents",
    naturalized_US_citizen: "Naturalized U.S. Citizen",
    not_US_citizen: "Not a U.S. Citizen",
  },

  // ------------------------------
  // Education & work
  // ------------------------------
  school_status: {
    student: "Student",
    not_student: "Not a Student",
  },

  educational_attainment: {
    none: "No Formal Schooling",
    less_than_hs: "Less than High School",
    hs_GED_equivalent: "High School / GED",
    some_college: "Some College",
    bachelor_associate_degree: "Associate / Bachelor’s Degree",
    masters_professional_doctorate: "Master’s / Professional / Doctorate",
  },

  employment_status: {
    employed: "Employed",
    unemployed: "Unemployed",
    army: "Armed Forces",
    not_in_labor_force: "Not in Labor Force",
  },

  worker_class: {
    private: "Private Wage & Salary",
    government: "Government",
    self_employed: "Self-Employed",
    unpaid: "Unpaid Family Worker",
    unemployed: "Unemployed / Not Working",
  },

  // industry_code is left numeric/coded; no labeled levels here.

  // ------------------------------
  // Commute
  // ------------------------------
  commute_mode: {
    car_truck_van: "Car / Truck / Van",
    bus: "Bus",
    streetcar_trolleycar: "Streetcar / Trolley",
    subway_elevated: "Subway / Elevated",
    railroad: "Railroad",
    ferryboat: "Ferryboat",
    taxicab: "Taxi",
    motorcycle: "Motorcycle",
    bicycle: "Bicycle",
    walk: "Walk",
    wfh: "Work From Home",
    other: "Other Mode",
  },

  commute_range: {
    "0 / work_from_home": "0 minutes / Work From Home",
    "1-14": "1–14 minutes",
    "15-29": "15–29 minutes",
    "30-44": "30–44 minutes",
    "45-59": "45–59 minutes",
    "60-89": "60–89 minutes",
    "90+": "90+ minutes",
  },

  // ------------------------------
  // Disability
  // ------------------------------
  disability_status: {
    with_disability: "With a Disability",
    without_disability: "Without a Disability",
  },

  hearing_diff: { yes: "Yes", no: "No" },
  vision_diff: { yes: "Yes", no: "No" },
  cognitive_diff: { yes: "Yes", no: "No" },
  ambulatory_diff: { yes: "Yes", no: "No" },
  self_care_diff: { yes: "Yes", no: "No" },
  indep_living_diff: { yes: "Yes", no: "No" },

  // ------------------------------
  // Health insurance
  // ------------------------------
  hins_status: {
    yes: "With Health Insurance",
    no: "No Health Insurance",
  },

  privcov: { yes: "Has Private Coverage", no: "No Private Coverage" },
  pubcov: { yes: "Has Public Coverage", no: "No Public Coverage" },

  hins_emp_union: { yes: "Employer / Union Coverage", no: "No Employer / Union Coverage" },
  hins_direct: { yes: "Direct Purchase Coverage", no: "No Direct Purchase Coverage" },
  hins_medicare: { yes: "Medicare", no: "No Medicare" },
  hins_medicaid: { yes: "Medicaid", no: "No Medicaid" },
  hins_tricare: { yes: "TRICARE / Military Health", no: "No TRICARE / Military Health" },
  hins_va: { yes: "VA Health Care", no: "No VA Health Care" },
  hins_ihs: { yes: "Indian Health Service", no: "No Indian Health Service" },

  // ------------------------------
  // Other insurance
  // ------------------------------
  fire_hazard_flood_insurance: {
    yes: "Has Fire / Hazard / Flood Insurance",
    no: "No Fire / Hazard / Flood Insurance",
  },

  // ------------------------------
  // Income supports
  // ------------------------------
  public_assistance_income: { yes: "Yes", no: "No" },

  // ------------------------------
  // Water heater attributes
  // ------------------------------
  main_water_heat_loc: {
    basement: "Basement",
    living_space: "Living Space",
    garage: "Garage",
    outside: "Outside",
    other: "Other",
  },

  main_water_heat_size: {
    small: "Small",
    med: "Medium",
    large: "Large",
    tankless: "Tankless",
  },

  main_water_heat_blanket: {
    insulated: "Insulated",
    not_insul: "Not Insulated",
  },

  main_water_heat_age: {
    u5: "<5 years",
    "5_9": "5–9 years",
    "10_19": "10–19 years",
    years20: "20+ years",
  },

  main_water_heat_fuel: {
    combust: "Combustion",
    elec: "Electric",
    wood: "Wood",
  },

  energy_assist: {
    "0": "No",
    "1": "Yes"
  },

  insulated: {
    well: "Well Insulated",
    adequate: "Adequately Insulated",
    poorly: "Poorly Insulated",
    not: "Not Insulated"
  },

  main_space_heat_equip: {
    central_furnace: "Central Furnace",
    central_heat_pump: "Central Heat Pump",
    ductless_heat_pump: "Ductless Heat Pump",
    hot_water_radiator: "Hot Water Radiator",
    built_in_electric_units: "Built-in Electric Units",
    built_in_room_heater: "Built-in Room Heater",
    portable_electric_heaters: "Portable Electric Heaters",
    wood_stove: "Wood Stove",
    Other: "Other"
  },

  main_space_heat_equip_age: {
    "1_10": "1–10 years",
    "10_20": "10–20 years",
    plus20: "20+ years",
    not_applicable: "Not Applicable"
  },

  hp_status: {
    already_has_hp: "Has Heat Pump",
    eligible_central_hp: "Central Heat Pump Eligible",
    eligible_ductless_hp: "Ductless Heat Pump Eligible",
    uncertain: "Uncertain Eligibility"
  },

  n_ductless_heatpumps: {
    "1": "1 Unit",
    "2_3": "2–3 Units",
    plus4: "4+ Units",
    not_applicable: "Not Applicable"
  },

  main_ac_equp: {
    central: "Central Air Conditioning",
    window_or_wall_ac: "Window or Wall Units",
    ductless_heat_pump: "Ductless Heat Pump",
    portable_ac: "Portable AC",
    not_applicable: "Not Applicable"
  },

    // ------------------------------
  // VELUX-relevant housing attributes
  // ------------------------------
  attic: {
    yes: "Has Attic",
    no: "No Attic",
  },

  fin_attic: {
    yes: "Finished Attic",
    no: "Unfinished Attic",
  },

  orig_win: {
    yes: "Original Windows",
    no: "Replacement Windows",
  },

  type_glass: {
    single: "Single Pane",
    double: "Double Pane",
    triple: "Triple Pane",
  },

  high_ceil: {
    yes: "High Ceilings",
    no: "No High Ceilings",
  },

  roof_mat: {
    asphalt: "Asphalt",
    clay: "Clay / Tile",
    metal: "Metal",
    other: "Other",
    slate: "Slate",
    wood: "Wood",
  },

};

// -----------------------------------------------------------------------------
// COMPOSITE CONFIGURATION 
// -----------------------------------------------------------------------------
// Defines variables that act as "Parents" in the UI but don't have a single
// underlying value. The UI will render these as a list of sub-questions.
export const compositeConfig = {
  heating: [
    { key: "main_space_heat_equip", label: "Primary Heating Equipment" },
    { key: "main_space_heat_equip_age", label: "Heating Equipment Age" },
  ],
  water_heater: [
    { key: "main_water_heat_loc", label: "Water Heater Location" },
    { key: "main_water_heat_size", label: "Water Heater Size" },
    { key: "main_water_heat_blanket", label: "Water Heater Insulation" },
    { key: "main_water_heat_age", label: "Water Heater Age" },
    { key: "main_water_heat_fuel", label: "Water Heater Fuel Type" },
  ],
  heat_pumps: [
    { key: "hp_status", label: "Heat Pump Eligibility" },
    { key: "n_ductless_heatpumps", label: "Number of Ductless Units" },
  ],
  windows: [
    { key: "orig_win", label: "Original Windows" },
    { key: "type_glass", label: "Window Glass Type" },
  ],
};

// -----------------------------------------------------------------------------
// OVERLAY CONFIGURATION 
// -----------------------------------------------------------------------------
export const overlayDefinitions = [
  {
    key: "hardware_stores",
    label: "Hardware Stores",
    color: "#00FF3B",
    stroke: "#003B10",
    brands: ["All brands", "Lowe's", "Home Depot", "Ace"],
  },
  {
    key: "food_stores",
    label: "Food Retailers",
    color: "#FF007F",
    stroke: "#5A003F",
    brands: [
      "All brands",
      "Walmart Store & Supercenter",
      "Albertsons",
      "Albertsons Market",
      "ACME Markets",
      "Andronico's Community Market",
      "Amigos United",
      "Balduccis",
      "Carrs Supermarket",
      "Haggen Food & Pharmacy",
      "Jewel Osco",
      "Kings Food Markets",
      "Lucky Stores",
      "Market Street",
      "Pavillions",
      "Randalls",
      "Safeway Inc",
      "Shaw's Supermarkets",
      "Star Market",
      "Tom Thumb",
      "United Express",
      "United Supermarkets",
      "VONS",
    ],
  },
  // To add a new layer later, just add an object here! 
  // e.g. { key: "pharmacies", label: "Pharmacies", color: "orange", ... }
];